import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react"

import {
  ESCAPE_SHORTCUT_PRIORITY,
  useEscapeShortcut,
} from "@/hooks/useEscapeShortcut"
import { useNavigationShortcuts } from "@/hooks/useNavigationShortcuts"
import { usePageShortcuts, type PageShortcut } from "@/hooks/usePageShortcuts"
import { useSearchShortcut } from "@/hooks/useSearchShortcut"

interface UseCollectionPageShortcutsOptions<T> {
  createAction?: () => void
  createLabel?: string
  enabled?: boolean
  getItemId: (item: T) => string
  itemLabel: string
  items: T[]
  onOpenItem: (item: T) => void
  onClearSearch?: () => void
  searchInputRef?: RefObject<HTMLInputElement | null>
  searchLabel?: string
  searchValue?: string
}

interface UseCollectionPageShortcutsResult<T> {
  activeIndex: number
  activeItem: T | null
  activeItemId: string | null
  clearActiveItem: () => void
  handlePointerEnter: (id: string | null) => void
  handlePointerLeave: (id: string | null) => void
  isActiveItem: (id: string) => boolean
  isKeyboardActive: boolean
  keyboardActiveItemId: string | null
  setActiveItemId: (id: string | null) => void
}

const EMPTY_INPUT_REF: RefObject<HTMLInputElement | null> = { current: null }

export function useCollectionPageShortcuts<T>({
  createAction,
  createLabel,
  enabled = true,
  getItemId,
  itemLabel,
  items,
  onOpenItem,
  onClearSearch,
  searchInputRef,
  searchLabel,
  searchValue,
}: UseCollectionPageShortcutsOptions<T>): UseCollectionPageShortcutsResult<T> {
  // Contract for collection/list surfaces:
  // - vertical movement: j/k, w/s, ArrowUp/ArrowDown
  // - Enter opens the active item
  // - pointer hover temporarily overrides the same active item state
  // - pointer leave restores the keyboard anchor instead of maintaining a
  //   second competing highlight/selection model
  const [activeItemState, setActiveItemState] = useState<{
    id: string
    source: "keyboard" | "pointer"
  } | null>(null)
  const keyboardAnchorRef = useRef<string | null>(null)
  const getItemIdRef = useRef(getItemId)
  const onOpenItemRef = useRef(onOpenItem)
  const createActionRef = useRef(createAction)

  getItemIdRef.current = getItemId
  onOpenItemRef.current = onOpenItem
  createActionRef.current = createAction

  const activeItemId = activeItemState?.id ?? null
  const keyboardActiveItemId = keyboardAnchorRef.current
  const isKeyboardActive = activeItemState?.source === "keyboard"

  const activeIndex = useMemo(() => {
    if (!activeItemId) {
      return -1
    }

    return items.findIndex(
      (item) => getItemIdRef.current(item) === activeItemId,
    )
  }, [activeItemId, items])

  const activeItem = activeIndex >= 0 ? (items[activeIndex] ?? null) : null

  useEffect(() => {
    if (
      activeItemId &&
      !items.some((item) => getItemIdRef.current(item) === activeItemId)
    ) {
      setActiveItemState(null)
    }

    if (
      keyboardAnchorRef.current &&
      !items.some(
        (item) => getItemIdRef.current(item) === keyboardAnchorRef.current,
      )
    ) {
      keyboardAnchorRef.current = null
    }
  }, [activeItemId, items])

  const clearActiveItem = useCallback(() => {
    keyboardAnchorRef.current = null
    setActiveItemState(null)
  }, [])

  const setActiveItemId = useCallback((id: string | null) => {
    keyboardAnchorRef.current = id
    setActiveItemState(
      id
        ? {
            id,
            source: "keyboard",
          }
        : null,
    )
  }, [])

  const handlePointerEnter = useCallback((id: string | null) => {
    setActiveItemState(
      id
        ? {
            id,
            source: "pointer",
          }
        : null,
    )
  }, [])

  const handlePointerLeave = useCallback((id: string | null) => {
    setActiveItemState((currentState) => {
      if (currentState?.source !== "pointer" || currentState.id !== id) {
        return currentState
      }

      if (keyboardAnchorRef.current) {
        return {
          id: keyboardAnchorRef.current,
          source: "keyboard",
        }
      }

      return null
    })
  }, [])

  const selectNext = useCallback(() => {
    if (items.length === 0) {
      return
    }

    const nextIndex =
      activeIndex < 0 ? 0 : Math.min(activeIndex + 1, items.length - 1)
    const nextItem = items[nextIndex]
    if (nextItem) {
      const nextItemId = getItemIdRef.current(nextItem)
      keyboardAnchorRef.current = nextItemId
      setActiveItemState({
        id: nextItemId,
        source: "keyboard",
      })
    }
  }, [activeIndex, items])

  const selectPrevious = useCallback(() => {
    if (items.length === 0) {
      return
    }

    const previousIndex =
      activeIndex < 0 ? items.length - 1 : Math.max(activeIndex - 1, 0)
    const previousItem = items[previousIndex]
    if (previousItem) {
      const previousItemId = getItemIdRef.current(previousItem)
      keyboardAnchorRef.current = previousItemId
      setActiveItemState({
        id: previousItemId,
        source: "keyboard",
      })
    }
  }, [activeIndex, items])

  const openActiveItem = useCallback(() => {
    if (!activeItem) {
      return
    }

    onOpenItemRef.current(activeItem)
  }, [activeItem])

  useNavigationShortcuts({
    enabled: enabled && items.length > 0,
    includeVerticalArrows: true,
    nextAliases: ["s"],
    nextKey: "j",
    nextLabel: `Next ${itemLabel}`,
    onNext: selectNext,
    onPrevious: selectPrevious,
    previousAliases: ["w"],
    previousKey: "k",
    previousLabel: `Previous ${itemLabel}`,
  })

  useEscapeShortcut({
    description: `Clear selected ${itemLabel}`,
    enabled: enabled && activeItemId !== null,
    handler: clearActiveItem,
    priority: ESCAPE_SHORTCUT_PRIORITY.ACTIVE_ITEM_CLEAR,
  })

  const pageShortcuts = useMemo((): PageShortcut[] => {
    const shortcuts: PageShortcut[] = [
      {
        action: openActiveItem,
        command: {
          group: "selection",
          kind: "open",
          scope: "selection",
        },
        enabled: enabled && activeItem !== null,
        key: "Enter",
        label: `Open selected ${itemLabel}`,
      },
    ]

    if (createAction && createLabel) {
      shortcuts.push({
        action: () => {
          createActionRef.current?.()
        },
        command: {
          kind: "create",
          scope: "page",
        },
        enabled,
        key: "n",
        label: createLabel,
      })
    }

    return shortcuts
  }, [
    activeItem,
    createAction,
    createLabel,
    enabled,
    itemLabel,
    openActiveItem,
  ])

  usePageShortcuts(pageShortcuts, enabled)

  useSearchShortcut({
    enabled: enabled && !!searchInputRef,
    inputRef: searchInputRef ?? EMPTY_INPUT_REF,
    label: searchLabel ?? `Search ${itemLabel}s`,
    onClear: onClearSearch,
    value: searchValue,
  })

  const isActiveItem = useCallback(
    (id: string) => activeItemId === id,
    [activeItemId],
  )

  return {
    activeIndex,
    activeItem,
    activeItemId,
    clearActiveItem,
    handlePointerEnter,
    handlePointerLeave,
    isActiveItem,
    isKeyboardActive,
    keyboardActiveItemId,
    setActiveItemId,
  }
}
